import React, { useEffect, useState } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { doc, getFirestore, onSnapshot, updateDoc } from 'firebase/firestore';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { ArrowLeft, Plug, Save, X, Edit } from 'lucide-react';

function HotplugSettingsViewer({ device, onBack }) {
  const [settings, setSettings] = useState(null);  // null = loading, {} = empty doc
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const familyID = device?.id;
  const deviceName = device?.familyName || familyID;

  useEffect(() => {
    if (!familyID) return;
    const db = getFirestore(app);
    const docRef = doc(db, 'deviceManagement', familyID, 'hotplug', 'settings');

    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        setSettings(snapshot.data());
      } else {
        setSettings({});
      }
    }, (error) => {
      console.error('HotplugSettingsViewer: listener error:', error);
    });

    return () => unsubscribe();
  }, [familyID]);

  const startEditing = (key, currentValue) => {
    setEditingKey(key);
    setEditValue(String(currentValue));
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const saveEdit = async (key, originalValue) => {
    setSaving(true);
    try {
      // Preserve the original type when saving
      let parsed = editValue;
      if (typeof originalValue === 'boolean') {
        parsed = editValue === 'true';
      } else if (typeof originalValue === 'number') {
        parsed = Number(editValue);
        if (isNaN(parsed)) {
          alert('Invalid number');
          setSaving(false);
          return;
        }
      }

      const db = getFirestore(app);
      const docRef = doc(db, 'deviceManagement', familyID, 'hotplug', 'settings');
      await updateDoc(docRef, { [key]: parsed });
      cancelEditing();
    } catch (error) {
      console.error('HotplugSettingsViewer: save error:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatValue = (value) => {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const entries = settings ? Object.entries(settings).sort(([a], [b]) => a.localeCompare(b)) : [];

  return (
    <div className="mx-auto p-6 min-h-screen flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Device Management
        </Button>
        <Plug className="w-8 h-8" />
        <div>
          <h1 className="text-3xl font-bold">Hotplug Settings</h1>
          <p className="text-sm text-muted-foreground">{deviceName}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Live values for this device. Changes take effect within seconds. The device resets all
        values to defaults on sign-out.
      </p>

      {settings === null ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Plug className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hotplug settings available.</p>
            <p className="text-xs mt-1">The device has no declared hotplug settings, or has gone offline.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-2 pb-2 px-2">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="py-1 px-2 text-left font-normal">Key</th>
                  <th className="py-1 px-2 text-left font-normal">Value</th>
                  <th className="py-1 px-2 text-left font-normal">Type</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(([key, value], index) => {
                  const isEditing = editingKey === key;
                  return (
                    <tr key={key} className={index % 2 === 0 ? 'bg-muted/60' : ''}>
                      <td className="py-1.5 px-2 text-sm font-mono align-middle">{key}</td>
                      <td className="py-1.5 px-2 align-middle">
                        <div className="flex items-center gap-1">
                          {isEditing ? (
                            <>
                              {typeof value === 'boolean' ? (
                                <select
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="h-7 text-xs border rounded px-1"
                                >
                                  <option value="true">true</option>
                                  <option value="false">false</option>
                                </select>
                              ) : (
                                <Input
                                  type={typeof value === 'number' ? 'number' : 'text'}
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="h-7 text-xs flex-1 min-w-[120px]"
                                  autoFocus
                                  step={typeof value === 'number' ? 'any' : undefined}
                                />
                              )}
                              <Button
                                size="sm"
                                onClick={() => saveEdit(key, value)}
                                disabled={saving}
                                className="h-7 w-7 p-0 flex-shrink-0"
                              >
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEditing}
                                disabled={saving}
                                className="h-7 w-7 p-0 flex-shrink-0"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <span className="text-sm font-mono">{formatValue(value)}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => startEditing(key, value)}
                                className="h-6 w-6 p-1 opacity-60 hover:opacity-100 transition-opacity hover:bg-gray-100 border border-gray-300 bg-white/80 flex-shrink-0"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-1.5 px-2 text-xs text-muted-foreground align-middle">
                        {typeof value}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default HotplugSettingsViewer;
